import { themeColor, themeFontFamily } from 'LibNeo'
import styled, { css } from 'styled-components'
import NumberInput from 'react-number-format'

export interface FlagInputProps {
	hasError?: boolean
	empty?: boolean
	disabled?: boolean
	readonly?: boolean
	hasPrefix?: boolean
	hasSuffix?: boolean
}

const ErrorCss = css`
	&,
	&:hover,
	&:focus {
		box-shadow: none;
		border-color: ${themeColor('pink', 5)};
	}
	border-color: ${themeColor('pink', 5)};

	& .ant-input,
	& .ant-input:hover,
	& .ant-input:focus {
		border: 0;
		box-shadow: none;
	}

	.ant-form-explain {
		color: ${themeColor('pink', 5)};
	}
`

const WarningCss = css`
	border-color: ${themeColor('sun')};
`

const PrefixCss = css`
	&,
	&.ant-input-affix-wrapper-input-with-clear-btn {
		.ant-input,
		.ant-input:not(:first-child) {
			padding-left: 9px;
		}
	}
`

const SuffixCss = css`
	&,
	&.ant-input-affix-wrapper-input-with-clear-btn {
		.ant-input,
		.ant-input:not(:last-child) {
			padding-right: 9px;
		}
	}
`

const DisabledReadonlyCss = css`
	background-color: ${themeColor('gray', 1)};
	border-color: ${themeColor('gray', 3)};
	color: ${themeColor('gray', 6)};
	cursor: not-allowed;

	&:hover,
	&:active,
	&:focus {
		border-color: ${themeColor('gray', 3)};
	}

	.ant-input-suffix,
	.ant-input-prefix {
		cursor: not-allowed;
		pointer-events: none;
	}
`

const ReadonlyCss = css`
	&,
	&:hover {
		cursor: default;
	}
`

export const InputCss = css<FlagInputProps>`
position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  min-height: 40px;
  padding: 0;
  border: 1px solid ${themeColor('gray', 3)};
  border-radius: 6px;
  font-family: ${themeFontFamily('SBSansInterface')};
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;
  outline: none;
  color: ${themeColor('gray', 8)};
  background-color: ${themeColor('white')};
  overflow: hidden;
  box-shadow: none;
  box-sizing: border-box;

&::placeholder {
    color: ${themeColor('gray', 4)};
  }

&:hover {
    border-color: ${themeColor('emerald', 5)};
    box-shadow: none;
  }

&:active,
&:focus,
&--focus {
    border-color: ${themeColor('emerald', 5)};
    box-shadow: none;
  }

&.ant-input[disabled] {
    ${DisabledReadonlyCss}
  }
  ${({ readonly, disabled }) => (readonly || disabled) && DisabledReadonlyCss}
  ${({ readonly }) => readonly && ReadonlyCss}
  ${({ hasError }) => hasError && ErrorCss}
  ${({ empty }) => empty && WarningCss}
  ${({ hasPrefix }) => hasPrefix && PrefixCss}
  ${({ hasSuffix }) => hasSuffix && SuffixCss}

&.ant-input {
    padding: 10px 16px;
  }

.ant-input:not(:last-child) {
    padding-right: 9px;
  }

.ant-input:not(:first-child) {
    padding-left: 9px;
  }

.ant-input {
    padding: 10px 16px;
    outline: none;
    border: none;
    box-shadow: none;
    color: ${themeColor('extraBackground3')};
    font-family: ${themeFontFamily('SBSansInterface')};
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 20px;
    height: auto;

  &[disabled] {
      background-color: transparent;
    }
  }

.ant-input-group-addon {
    border: none;
    background-color ${themeColor('white')};
    font-size: 14px;
    color: ${themeColor('pink', 7)};
    padding: 0;
  }

.ant-input-prefix,
.ant-input-suffix {
    position: relative;
    left: auto;
    top: auto;
    right: auto;
    bottom: auto;
    transform: none;
    cursor: pointer;
  }

.ant-input-prefix {
    padding-left: 10px;
  }

.ant-input-suffix {
    padding-right: 11px;
    color: ${themeColor('gray', 4)};

    svg {
      fill: ${themeColor('gray', 4)};
    }

  &:hover {
      color: ${themeColor('gray', 8)};

      svg {
        fill: ${themeColor('gray', 8)};
      }
    }
  }

  .ant-input-group-wrapper {
    height: 56px;
  }
  
  .ant-input-affix-wrapper {
    .ant-input-prefix,
    .ant-input-suffix {
      :not(.anticon) {
        line-height: 17px;
      }
    }
  }
  
  .ant-input-clear-icon {
    & svg {
      visibility: hidden;
    }

    &:before {
      content: "";
      position: absolute;
      display: block;
      visibility: hidden;
      margin-top: -4px;
      margin-left: -8px;
      height: 20px;
      width: 20px;
      background-color: ${themeColor('gray', 4)};
      mask-image: url('/svg/icon-close.svg');
      -webkit-mask-image: url('/svg/icon-close.svg');
    }

    &:not(:last-child) {
      padding-right: 4px;
    }
  }

  .ant-input-clear-icon {
    &:before {
      visibility: visible;
    }

    &:hover {
      &:before {
        background-color: ${themeColor('gray', 8)};
      }
    }
  }
  
  .ant-select-disabled {
    opacity: 0.6;

    & .ant-select-selection {
      background-color: transparent;
    }
  }
`

export const WrapperMessage = styled.div<{ visible?: boolean }>`
	color: ${themeColor('pink', 5)};
	font-family: ${themeFontFamily('SBSansInterface')};
	font-style: normal;
	font-weight: normal;
	font-size: 12px;
	line-height: 20px;
	padding: 4px 0 0;
	display: none;
	transition: all 0.6s linear;
	${({ visible }) =>
		visible &&
		css`
			display: block;
			transition: all 0.6s linear;
		`}
`

export const WrapperContainer = styled.div<{ hidden?: boolean }>`
	position: relative;

	.ant-form-item-control {
		line-height: 18px;
	}

	.ant-form-item-children {
		display: block;
	}

	.ant-form-explain {
		margin: 4px 0 0 0;
		font-family: ${themeFontFamily('SBSansInterface')};
		font-size: 12px !important;
		line-height: 20px !important;
		min-height: auto;
		color: ${themeColor('pink', 5)};
	}
	.ant-col {
		min-height: 0;
	}

	${({ hidden }) =>
		hidden &&
		css`
			height: 0;
			overflow: hidden;
		`}
	.has-error {
		.ant-input {
			border-color: ${themeColor('pink', 5)};

			&:focus,
			&:hover {
				box-shadow: none;
			}

			.ant-form-explain {
				color: ${themeColor('pink', 5)};
				display: block;
			}
		}
	}
`

export const WrapperHelpText = styled.span`
	padding-top: 4px;
	font-size: 12px;
	line-height: 20px;
	color: ${themeColor('gray', 5)};
`

export const WrapperInputNumber = styled(NumberInput)<FlagInputProps>`
	${InputCss}
`
